import { useCallback } from 'react';
import type { GoodsOption } from 'shared/src/api';
import { useProductCheckout } from '../queries/useProductCheckout';
import { useLogClickCheckOut } from './useLogClickCheckOut';
import { useMyInfo } from 'shared/src/queries';
import { useOpenDeeplink } from 'shared/src/hooks';
import { bridge, getWebBaseURL, isWeb } from 'shared/src/utils';
import { ApiError } from 'shared/src/api/httpClient';
import { systemAlert } from 'shared/src/component';
import * as Sentry from '@sentry/react-native';

export const useOnPressPurchase = ({
  selected,
  onCompletePurchase,
  onAnonymouseUserPressedPurchase,
}: {
  selected: GoodsOption[];
  onCompletePurchase: () => void;
  onAnonymouseUserPressedPurchase: () => void;
}) => {
  const { mutateAsync: checkoutProduct, isPending: loadingProductCheckout } =
    useProductCheckout(selected);
  const { data: myInfo } = useMyInfo();
  const logClickCheckOut = useLogClickCheckOut({ selected });
  const { openDeeplink } = useOpenDeeplink();

  const onPressPurchase = useCallback(async () => {
    if (myInfo?.is_anonymous) {
      onAnonymouseUserPressedPurchase();
      return;
    }
    logClickCheckOut();
    try {
      const result = await checkoutProduct();

      if (result) {
        if (isWeb) {
          const baseURL = await getWebBaseURL();
          location.href = `${baseURL}/payment/checkout/${result.checkout_key}`;
        } else {
          openDeeplink(result.deeplink);
        }
        onCompletePurchase();
      }
    } catch (ex) {
      if (ex instanceof ApiError) {
        if (ex.type === 'ALERT') {
          systemAlert(ex.message);
        } else {
          Sentry.captureException(ex);
          bridge.toast(ex.message);
        }
      }
    }
  }, [
    checkoutProduct,
    logClickCheckOut,
    myInfo?.is_anonymous,
    onAnonymouseUserPressedPurchase,
    onCompletePurchase,
    openDeeplink,
  ]);

  return { onPressPurchase, loadingProductCheckout };
};
